/**
 * OD-PARITY-001 — which *presentation* the operational shell should render.
 *
 * `isOrderDeskView` is true for the native order_desk role OR when the active
 * workspace is Order Desk and the user holds `orders:view`. It selects Order
 * Desk components/layout ONLY (header style, KPI cards, tabs, search card,
 * table cosmetics, Favorites, report panel). Capabilities (New Order, Edit,
 * Reports, Bulk Upload …) stay gated by effective permissions / role.
 */
import { useLocation } from 'react-router-dom';
import { usePermissions } from './usePermissions';
import { deriveWorkspaces, resolveActiveWorkspace } from '../utils/workspaces';

const normalizeRole = (r) => (r || '').toLowerCase().replace(/[\s-]+/g, '_');

export const useWorkspacePresentation = (user) => {
  const location = useLocation();
  const { permissions, hasPermission } = usePermissions();
  const role = normalizeRole(user?.role);
  const nativeOrderDesk = role === 'order_desk';
  const active = resolveActiveWorkspace(deriveWorkspaces(user, permissions), location.pathname);
  // RBAC-WS-SEARCH-001/002: matrix-granted processor workspaces (deriveWorkspaces already
  // applied the qc_product_scope rule, so presence in the list == effective entitlement).
  const isTitleSearchView = active?.id === 'title_search';
  const isTaxProcessorView = active?.id === 'tax_processor';
  const isProcessorWorkspaceView = isTitleSearchView || isTaxProcessorView;
  // OD-DUAL-ROLE-002: a native Order Desk user who has SWITCHED to a processor workspace (held Search Processor
  // role) is presented as that workspace — Order Desk-only presentation (manual linking, OD back-nav) is off there.
  const isOrderDeskView = (nativeOrderDesk && !isProcessorWorkspaceView)
    || (active?.id === 'order_desk' && hasPermission('orders', 'view'));
  return {
    isOrderDeskView,
    isTitleSearchView,
    isTaxProcessorView,
    isProcessorWorkspaceView,
    activeWorkspace: active,
    nativeOrderDesk,
    presentation: isOrderDeskView ? 'order_desk' : (isProcessorWorkspaceView ? active.presentAs : role),
    hasPermission,
  };
};

export default useWorkspacePresentation;
